/**
 * Utility functions to format act content from nested dictionary structures.
 * Handles the nested structure with empty keys that comes from the backend.
 */
import React from 'react';

/**
 * Recursively extracts text content from nested dictionaries with empty keys.
 * Similar to the Python assemble_article_content logic.
 */
export function extractTextFromNestedDict(data: any): string {
  if (typeof data === 'string') {
    return data.trim();
  }

  if (data === null || data === undefined) {
    return '';
  }

  if (typeof data !== 'object') {
    return String(data).trim();
  }

  // If it's an array, process each item
  if (Array.isArray(data)) {
    return data.map(item => extractTextFromNestedDict(item)).filter(Boolean).join(' ');
  }

  // If it's an object/dict
  const parts: string[] = [];

  function formatContent(value: any, depth: number = 0): void {
    if (typeof value !== 'object' || value === null) {
      if (value && String(value).trim()) {
        parts.push(String(value).trim());
      }
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(item => formatContent(item, depth));
      return;
    }

    // Process dictionary keys
    for (const [k, v] of Object.entries(value)) {
      if (k === '') {
        // Empty keys - content without numbering
        if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
          formatContent(v, depth);
        } else {
          if (v && String(v).trim()) {
            parts.push(String(v).trim());
          }
        }
      } else {
        // Non-empty keys - elements with numbering
        if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
          // Check if dict has empty key with text as first element
          if ('' in v) {
            const emptyContent = v[''];
            if (typeof emptyContent !== 'object' || emptyContent === null || Array.isArray(emptyContent)) {
              if (emptyContent && String(emptyContent).trim()) {
                // Key + text on same line
                parts.push(`${k} ${String(emptyContent).trim()}`);
                // Process remaining elements (without empty key)
                const remaining: Record<string, any> = {};
                for (const [k2, v2] of Object.entries(v)) {
                  if (k2 !== '') {
                    remaining[k2] = v2;
                  }
                }
                if (Object.keys(remaining).length > 0) {
                  formatContent(remaining, depth + 1);
                }
              } else {
                // Key separately, then further processing
                parts.push(k);
                formatContent(v, depth + 1);
              }
            } else {
              // Key separately, then further processing
              parts.push(k);
              formatContent(v, depth + 1);
            }
          } else {
            // Key separately, then further processing
            parts.push(k);
            formatContent(v, depth + 1);
          }
        } else {
          // Key + text on same line
          if (v && String(v).trim()) {
            parts.push(`${k} ${String(v).trim()}`);
          }
        }
      }
    }
  }

  formatContent(data, 0);
  return parts.join(' ').trim();
}

/**
 * Renders `<sup>` markup as real superscripts and nothing else.
 *
 * Backend text carries article indices as literal tags ("art. 479<sup>33</sup>"),
 * which print as visible markup when dropped into JSX. Use this wherever the
 * text must stay byte-identical apart from the tags - diff tokens, labels -
 * i.e. where formatArticleContent's spacing repairs would be wrong.
 */
export function renderSupMarkup(text: string): React.ReactNode {
  if (!text) return text;
  if (!/<sup/i.test(text)) return text;

  const parts = text.split(/(<sup[^>]*>|<\/sup>)/gi);
  const result: React.ReactNode[] = [];
  let isSup = false;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (/<sup[^>]*>/i.test(part)) {
      isSup = true;
    } else if (/<\/sup>/i.test(part)) {
      isSup = false;
    } else if (part !== '') {
      result.push(isSup ? <sup key={i}>{part}</sup> : part);
    }
  }

  return <>{result}</>;
}

/**
 * The text half of formatArticleContent: unwraps nested-dict content and
 * repairs the missing spaces, but leaves the `<sup>` tags in the string. The
 * act page's server-side HTML renderer (actBodyHtml.ts) builds its markup from
 * this, so the React and the string renderings cannot drift apart.
 */
export function prepareArticleText(content: string | any): string {
  if (!content) {
    return '';
  }

  let text = '';

  // If it's already a string, return it
  if (typeof content === 'string') {
    // Check if it's a string representation of a dict (like "{'': {'': 'content'}}")
    // This shouldn't happen with proper backend fix, but handle it as fallback
    text = content.trim();
    if (text.startsWith('{') && text.includes("'':")) {
      try {
        // Try to parse it as JSON-like structure
        // Replace single quotes with double quotes for JSON parsing
        const jsonLike = text.replace(/'/g, '"');
        const parsed = JSON.parse(jsonLike);
        text = extractTextFromNestedDict(parsed);
      } catch {
        // If parsing fails, just keep the string
      }
    }
  } else {
    // If it's an object, extract text recursively
    text = extractTextFromNestedDict(content);
  }

  // Fix spacing: Ensure space after Art. X., § Y., or numbers followed by text
  // Example: "Art. 23.Do" -> "Art. 23. Do"
  // Look for: (Art.|§|digit) + dot + (optionally superscripts but we handle text here) + immediate letter
  // Note: Since 'sup' is tag, regex on string might see "Art. 1<sup>1</sup>.Text".
  // So we first fix spacing where possible, then parse tags.

  // Fix spacing: Ensure space after Art. X., § Y., or numbers followed by text
  // Example: "Art. 23.Do" -> "Art. 23. Do"
  // Look for: (Art.|§|digit) + dot + (optionally superscripts but we handle text here) + immediate letter
  text = text.replace(/((?:Art\.|§)\s*\d+[a-z0-9]*\.|(?:\s|^)\d+\.)([A-ZŚĆŹŻŁÓa-zśćźżłó])/g, '$1 $2');

  // Fix spacing after superscript ending with a dot: "</sup>.Text" -> "</sup>. Text"
  text = text.replace(/(<\/sup>\.)([A-ZŚĆŹŻŁÓa-zśćźżłó])/g, '$1 $2');

  return text;
}

/**
 * Formats article content, handling both string and nested dictionary formats.
 * Returns ReactNode to support HTML tags like <sup>.
 */
export function formatArticleContent(content: string | any): React.ReactNode {
  if (!content) {
    return '';
  }

  const text = prepareArticleText(content);

  // Convert to ReactNode array handling <sup> tags with attributes support
  // Matches <sup ...>...</sup> or matches just the tags to split content
  // Split by specific regex to capture tags
  const parts = text.split(/(<sup[^>]*>|<\/sup>)/gi);

  const result: React.ReactNode[] = [];
  let isSup = false;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    // Check if it's an opening tag
    if (/<sup[^>]*>/i.test(part)) {
      isSup = true;
    }
    // Check if it's a closing tag
    else if (/<\/sup>/i.test(part)) {
      isSup = false;
    }
    // It's content
    else if (part !== '') {
      if (isSup) {
        result.push(<sup key={i}>{part}</sup>);
      } else {
        result.push(part);
      }
    }
  }

  return <>{result}</>;
}
